import { yupResolver } from '@hookform/resolvers/yup';
import { Send } from '@mui/icons-material';
import { Avatar, Box, Stack, styled, Tooltip, Grid } from '@mui/material';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  WsSelectObj,
  Item,
  WsColors,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  WsSelect,
  timeSince,
  ControlledTextArea,
  WsButton,
} from 'wisestep-base';
import { object, string } from 'yup';

export interface IMsg {
  message: string;
  direction: 'INBOUND' | 'OUTBOUND';
  time: Date;
}

export interface IMessageData {
  from: string;
  message: string;
  to: string;
}

type NumberValidity = {
  country: string;
  isValid: boolean;
  number: string;
};

export interface IChatBoxProps {
  msgList: IMsg[];
  receiverEmail: string;
  receiverName: string;
  receiverNumber: string;
  senderEmail: string;
  fromNumbers: WsSelectObj[];
  sendMsg: (message: IMessageData) => Promise<void>;
  toNumbers: WsSelectObj[];
  checkNumber: (number: string) => NumberValidity;
  jobCode: string;
  jobTitle: string;
}

const schema = object().shape({
  from: string().required(),
  message: string().required().max(300, 'Cannot exceed 300 characters'),
  to: string().required(),
});

export const ChatContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  minHeight: 450,
  maxHeight: 450,
  overflowY: 'scroll',
  backgroundColor: WsColors.TertiaryShades50,
}));

export const ChatBoxHeader = styled(Grid)(({ theme }) => ({
  backgroundColor: WsColors.BorderGrey,
  padding: theme.spacing(1.5),
}));

export const ChatFooter = styled(Item)(({ theme }) => ({
  padding: theme.spacing(0),
  borderTop: `1px solid ${WsColors.TertiaryShades500}`,
  marginBottom: theme.spacing(-3),
}));

export const ChatTextContainer = styled(Item)(({ theme }) => ({
  padding: 0,
  borderRadius: theme.spacing(1),
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
}));
interface IWsChatBubbleProps {
  direction: 'INBOUND' | 'OUTBOUND';
}
const WsChatBubble = styled(Item)(({ direction }: IWsChatBubbleProps) => ({
  padding: 10,
  margin: 10,
  borderRadius: direction === 'OUTBOUND' ? '15px 15px 0 15px' : '15px 15px 15px 0',
  backgroundColor: direction === 'OUTBOUND' ? WsColors.TheBlues100 : WsColors.White,
  width: 'fit-content',
  maxWidth: 500,
  border: `1px solid ${
    direction === 'OUTBOUND' ? WsColors.TheBlues100 : WsColors.TertiaryShades300
  }`,
}));
const WsChatBoxNew = ({
  msgList,
  receiverEmail,
  receiverNumber,
  senderEmail,
  fromNumbers,
  sendMsg,
  toNumbers,
  checkNumber,
  jobCode,
  jobTitle,
}: IChatBoxProps) => {
  const methods = useForm<IMessageData>({
    resolver: yupResolver(schema),
    defaultValues: {
      from: fromNumbers[0].value.toString(),
      message: '',
      to: receiverNumber,
    },
  });

  const toPhoneNumber = methods.watch('to');

  const [characters, setCharacters] = useState<number>(0);

  const [isSendingSms, setIsSendingSms] = useState(false);

  const [numberValidity, setNumberValidity] = useState<NumberValidity>({
    number: receiverNumber,
    country: '',
    isValid: false,
  });

  const lastMessageRef = useRef<HTMLDivElement | null>(null);

  const handleSendSms = async (data: IMessageData) => {
    try {
      setIsSendingSms(true);
      await sendMsg(data);
    } finally {
      setIsSendingSms(false);
      methods.resetField('message');
    }
  };

  const scrollToBottom = () => lastMessageRef.current?.scrollIntoView({ behavior: 'smooth' });

  const updateMessage = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCharacters(ev.target.value.length);
    methods.setValue('message', ev.target.value);
  };

  useEffect(() => {
    scrollToBottom();
  }, [msgList]);

  useEffect(() => {
    const numberValidityResult = checkNumber(toPhoneNumber);
    setNumberValidity(numberValidityResult);
  }, [toPhoneNumber, checkNumber]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSendSms)}>
        <Box>
          <ChatBoxHeader container direction="row" alignItems={'center'} spacing={1}>
            <Grid item xs={4}>
              <WsTypography variant={WsTypoVariants.TITLE} bold={true} color={WsTypoColors.BLACK}>
                Send SMS
              </WsTypography>
              {/* <WsTypography variant={WsTypoVariants.TITLE}>{receiverName}</WsTypography> */}
            </Grid>
            <Grid item xs={4} alignItems={'center'}>
              <WsSelect
                name="from"
                variant="standard"
                selectArray={fromNumbers}
                defaultValue={fromNumbers[0] ? fromNumbers[0].label : ''}
              />
              {!numberValidity.isValid && (
                <WsTypography color={WsTypoColors.ERROR}>Invalid Number</WsTypography>
              )}
            </Grid>

            <Grid xs={4} display="flex" justifyContent="flex-end" pl={3} container item>
              <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
                {jobTitle}
              </WsTypography>
              <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
                {'(' + jobCode + ')'}
              </WsTypography>
            </Grid>
          </ChatBoxHeader>
          <ChatContainer>
            {msgList.map((msg: IMsg, key: number) => (
              <Stack
                key={key}
                alignItems={msg.direction === 'OUTBOUND' ? 'flex-end' : 'flex-start'}
              >
                <Stack px={8}>
                  <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.TERTIARY}>
                    {timeSince(msg.time.toString())}
                  </WsTypography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {msg.direction === 'INBOUND' && (
                    <Tooltip title={receiverEmail}>
                      <Avatar sx={{ bgcolor: '#C6BBF2' }}>{receiverEmail.substring(0, 1)}</Avatar>
                    </Tooltip>
                  )}
                  <WsChatBubble direction={msg.direction}>
                    <WsTypography>{msg.message}</WsTypography>
                  </WsChatBubble>
                  {msg.direction === 'OUTBOUND' && (
                    <Tooltip title={senderEmail}>
                      <Avatar sx={{ bgcolor: '#C6BBF2' }}>{senderEmail.substring(0, 1)}</Avatar>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            ))}
            <div ref={lastMessageRef} />
          </ChatContainer>
          <ChatFooter>
            <Grid item container xs={12} alignItems="center">
              <Grid item xs={0.5}>
                To :
              </Grid>
              <Grid item xs={3}>
                <WsSelect
                  name="to"
                  variant="standard"
                  selectArray={toNumbers}
                  defaultValue={receiverNumber}
                />
              </Grid>
            </Grid>
            <Stack direction="row" justifyContent="center" alignItems="center" py={2} gap={1}>
              <Grid item xs={10}>
                <ControlledTextArea
                  placeholder="Say something..."
                  name="message"
                  onChange={updateMessage}
                  maxLength={300}
                />
                <Grid item xs={12} justifyContent="flex-end" display="flex">
                  <WsTypography>{`Characters: ${characters}/300`}</WsTypography>
                </Grid>
              </Grid>
              <WsButton
                disabled={!numberValidity.isValid || isSendingSms}
                size={'large'}
                endIcon={<Send />}
                type="submit"
              >
                Send
              </WsButton>
            </Stack>
          </ChatFooter>
        </Box>
      </form>
    </FormProvider>
  );
};

export default WsChatBoxNew;
